import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type } from "arktype";
import { Controller, useForm } from "react-hook-form";

import { orpc } from "@/client";
import { PersonSearch, PersonSearchSchemas } from "@/components/medico/person-search";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OverlayShell } from "@/components/ui/overlay";
import { format } from "@/lib/format";

const attendanceForm = type({
	person: PersonSearchSchemas.chosen.or("null"),
	service_type: type.enumerated("Consulta", "Retorno", "Cirurgia", "Procedimento"),
	date: "string",
	start_time: "string",
	duration_minutes: "5 <= number.integer <= 720",
	description: "string",
}).narrow((value, ctx) => value.person !== null || ctx.mustBe("a person"));

export function AgendaAttendanceModal({ onClose }: { onClose: () => void }) {
	const queryClient = useQueryClient();
	const { mutate, isPending } = useMutation(
		orpc.agenda.createAttendance.mutationOptions({
			onSuccess: async () => {
				await queryClient.invalidateQueries({ queryKey: orpc.agenda.key() });
				onClose();
			},
		}),
	);
	const { control, register, handleSubmit } = useForm<typeof attendanceForm.infer>({
		resolver: arktypeResolver(attendanceForm),
		defaultValues: {
			person: null,
			service_type: "Consulta",
			date: format.today(),
			start_time: "09:00",
			duration_minutes: 60,
			description: "",
		},
	});

	return (
		<OverlayShell
			label="Registrar atendimento"
			onClose={onClose}
			className="items-center justify-center p-4"
		>
			<form
				onSubmit={handleSubmit((value) => {
					if (value.person === null) {
						return;
					}

					mutate({
						person_id:
							value.person.kind === "patient" ? value.person.patientId : value.person.leadId,
						service_type: value.service_type,
						date: value.date,
						start_time: value.start_time,
						duration_minutes: value.duration_minutes,
						description: value.description,
					});
				})}
				className="overlay-panel max-w-md gap-4 p-5"
			>
				<h2 className="type-section-title">Registrar atendimento</h2>
				<Controller
					control={control}
					name="person"
					render={({ field }) => (
						<PersonSearch chosen={field.value} onChoose={field.onChange} disabled={isPending} />
					)}
				/>
				<select
					{...register("service_type")}
					aria-label="Tipo de atendimento"
					className="control-field h-11 md:h-10"
				>
					<option>Consulta</option>
					<option>Retorno</option>
					<option>Cirurgia</option>
					<option>Procedimento</option>
				</select>
				<div className="grid grid-cols-3 gap-2">
					<Input {...register("date")} aria-label="Data do atendimento" type="date" />
					<Input {...register("start_time")} aria-label="Horário do atendimento" type="time" />
					<Input
						{...register("duration_minutes", { valueAsNumber: true })}
						aria-label="Duração em minutos"
						type="number"
						min={5}
						step={5}
					/>
				</div>
				<Input {...register("description")} aria-label="Descrição" placeholder="Descrição" />
				<div className="flex justify-end gap-2">
					<Button type="button" variant="ghost" onClick={onClose}>
						Cancelar
					</Button>
					<Button type="submit" disabled={isPending}>
						Registrar
					</Button>
				</div>
			</form>
		</OverlayShell>
	);
}
